import { pickCopy } from '@/lib/mycio/translations'
import { getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'

/**
 * Billing UI: the in-app /pricing page, the checkout errors of usePricingSection, the Paddle /pay page and the
 * settings CancelSubscriptionPanel.
 *
 * FOUNDER, R2 (2026-09-21): pricing, payment and "your data" pages are UI and are shown in the member's
 * language; only the legal CONTRACT (Terms of Use, Refund Policy and the other legal documents, linked from these
 * pages) stays English. Nothing in this module is contract text.
 *
 * House rules (as planWidgets.ts / dashboard.ts):
 * - `en` is byte-identical to the strings the components shipped with.
 * - Prices are never written into a string: `{plus}`, `{wealth}` and `{date}` are filled by the component with
 *   locale-formatted values; `{email}` and `{plan}` are filled verbatim.
 * - "Before/After" pairs wrap a link; a language whose word order puts the link elsewhere leaves one side empty.
 * - zh-Hant is derived from `zh` by pickCopy's opencc chokepoint; only author `zh` (Simplified) here.
 */
export type MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: string
    checkoutReturned: string
    sectionAria: string
    intro: string
    notAdviceBefore: string
    whatWeModel: string
    notAdviceMiddle: string
    seeExample: string
    notAdviceAfter: string
    selectionBefore: string
    selectionAfter: string
    /** "{plan}" = the plan's name. */
    continueWith: string
    titleBefore: string
    titleEm: string
    titleAfter: string
    body: string
    /** "{plus}" / "{wealth}" = each plan's annual renewal, locale-formatted. */
    renewalBody: string
    footer: string
    questionsLink: string
    privacyLink: string
  }
  checkout: {
    unavailable: string
    betaConfirm: string
  }
  pay: {
    opening: string
    open: string
    returning: string
    failedBefore: string
    returnToPricing: string
    failedAfter: string
    noCheckoutBefore: string
    choosePlan: string
    noCheckoutAfter: string
    unavailableBefore: string
    contactSupport: string
    unavailableAfter: string
  }
  cancel: {
    title: string
    body: string
    cta: string
    confirmTitle: string
    confirmBody: string
    reasonLabel: string
    reasonPlaceholder: string
    confirm: string
    keep: string
    working: string
    doneTitle: string
    doneBody: string
    /** "{date}" = the paid-through date, locale-formatted. */
    doneUntil: string
    doneNoSub: string
    /** "{email}" = the support address. */
    failed: string
  }
}

/** Native-speaker review status of the non-English copy in this module. */
export const NATIVE_REVIEW = {
  reviewed: false,
  authored: '2026-09-21 in-session',
  note: 'pricing, checkout, pay: all keys, 13 languages; cancel: all keys for pt it ru zh ja ko ar hi id tr (es fr de pre-existing)',
} as const

const en: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Dashboard',
    checkoutReturned: 'You returned from checkout. Review your billing status before trying again if you already submitted a payment.',
    sectionAria: 'Choose the right level of help',
    intro: 'Start with a financial question. Explore supported choices, understand the tradeoffs and work out practical next steps with MyCIO. Saved plans let you continue the conversation as your situation changes.',
    notAdviceBefore: 'MyCIO helps you plan what to do; it does not place trades or move your money. Check ',
    whatWeModel: 'what we model',
    notAdviceMiddle: ' and ',
    seeExample: 'see an example',
    notAdviceAfter: ' before choosing a paid plan.',
    selectionBefore: 'Your selection: ',
    selectionAfter: '. Review the price and terms before continuing; signing in does not start checkout.',
    continueWith: 'Continue with {plan}',
    titleBefore: 'Help for the question. ',
    titleEm: 'A plan for what comes next.',
    titleAfter: '',
    body: 'Choose how much ongoing help you need. Coverage limits apply to every tier; a purchase does not make an unsupported scenario available.',
    renewalBody: 'Plus and Wealth include the first year of Living Plan. The advertised annual renewal is {plus} for Plus and {wealth} for Wealth. Review the renewal terms before purchase, and contact us to confirm terms for a Cross-Border engagement.',
    footer: 'Prices shown are in USD. Review the total, billing interval and any applicable taxes shown at checkout before confirming payment.',
    questionsLink: 'Questions about a plan or renewal?',
    privacyLink: 'Privacy policy',
  },
  checkout: {
    unavailable: 'Checkout isn\'t available right now — please try again shortly.',
    betaConfirm: 'You currently have full beta access.\n\nBuying now charges the initial-year price shown at checkout. For Plus or Wealth, the lower annual renewal starts one year after purchase; it is not tied to when beta access ends.\n\nContinue to checkout?',
  },
  pay: {
    opening: 'Opening secure checkout…',
    open: 'Complete your purchase in the secure checkout window. If it did not open, allow pop-ups for this site and reload.',
    returning: 'Returning you to pricing…',
    failedBefore: 'The checkout could not be loaded. ',
    returnToPricing: 'Return to pricing',
    failedAfter: ' and try again.',
    noCheckoutBefore: 'There is no checkout to open. ',
    choosePlan: 'Choose a plan',
    noCheckoutAfter: ' to start one.',
    unavailableBefore: 'Checkout is temporarily unavailable. Please try again shortly or ',
    contactSupport: 'contact support',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Cancel subscription',
    body: 'Cancelling stops future renewals. You keep full access until the end of the period you have already paid for, and nothing is deleted.',
    cta: 'Cancel my subscription',
    confirmTitle: 'Cancel your subscription?',
    confirmBody: 'Your plan and data stay exactly as they are. You can resubscribe at any time.',
    reasonLabel: 'What made you cancel? (optional)',
    reasonPlaceholder: 'Anything you tell us here goes straight to the team.',
    confirm: 'Confirm cancellation',
    keep: 'Keep my subscription',
    working: 'Cancelling…',
    doneTitle: 'Your subscription is cancelled',
    doneBody: 'Thank you — that feedback goes to the team.',
    doneUntil: 'You keep full access until {date}.',
    doneNoSub: 'There was no recurring subscription active on this account, so nothing further will be charged.',
    failed: 'We could not cancel the subscription. Nothing has changed — please email {email} and we will handle it.',
  },
}

const es: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Panel',
    checkoutReturned: 'Has vuelto del pago. Si ya enviaste un pago, revisa el estado de tu facturación antes de volver a intentarlo.',
    sectionAria: 'Elige el nivel de ayuda adecuado',
    intro: 'Empieza con una pregunta financiera. Explora las opciones admitidas, entiende sus contrapartidas y define con MyCIO próximos pasos prácticos. Los planes guardados te permiten continuar la conversación a medida que cambia tu situación.',
    notAdviceBefore: 'MyCIO te ayuda a planificar qué hacer; no ejecuta operaciones ni mueve tu dinero. Consulta ',
    whatWeModel: 'qué modelizamos',
    notAdviceMiddle: ' y ',
    seeExample: 'mira un ejemplo',
    notAdviceAfter: ' antes de elegir un plan de pago.',
    selectionBefore: 'Tu selección: ',
    selectionAfter: '. Revisa el precio y las condiciones antes de continuar; iniciar sesión no inicia el pago.',
    continueWith: 'Continuar con {plan}',
    titleBefore: 'Ayuda para tu pregunta. ',
    titleEm: 'Un plan para lo que viene.',
    titleAfter: '',
    body: 'Elige cuánta ayuda continuada necesitas. Todos los niveles tienen límites de cobertura; una compra no hace disponible un escenario no admitido.',
    renewalBody: 'Plus y Wealth incluyen el primer año de Living Plan. La renovación anual anunciada es de {plus} para Plus y de {wealth} para Wealth. Revisa las condiciones de renovación antes de comprar y contáctanos para confirmar las condiciones de un encargo Transfronterizo.',
    footer: 'Los precios se muestran en USD. Revisa el total, la periodicidad de facturación y los impuestos aplicables que se muestran al pagar antes de confirmar el pago.',
    questionsLink: '¿Preguntas sobre un plan o su renovación?',
    privacyLink: 'Política de privacidad',
  },
  checkout: {
    unavailable: 'El pago no está disponible en este momento; inténtalo de nuevo en breve.',
    betaConfirm: 'Ahora mismo tienes acceso beta completo.\n\nSi compras ahora, se cobra el precio del primer año que se muestra al pagar. En Plus o Wealth, la renovación anual más baja empieza un año después de la compra; no depende de cuándo termine el acceso beta.\n\n¿Continuar al pago?',
  },
  pay: {
    opening: 'Abriendo el pago seguro…',
    open: 'Completa tu compra en la ventana de pago seguro. Si no se ha abierto, permite las ventanas emergentes de este sitio y vuelve a cargar la página.',
    returning: 'Volviendo a los precios…',
    failedBefore: 'No se ha podido cargar el pago. ',
    returnToPricing: 'Vuelve a los precios',
    failedAfter: ' e inténtalo de nuevo.',
    noCheckoutBefore: 'No hay ningún pago que abrir. ',
    choosePlan: 'Elige un plan',
    noCheckoutAfter: ' para empezar uno.',
    unavailableBefore: 'El pago no está disponible temporalmente. Inténtalo de nuevo en breve o ',
    contactSupport: 'contacta con soporte',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Cancelar suscripción',
    body: 'Cancelar detiene las renovaciones futuras. Mantienes el acceso completo hasta el final del periodo que ya has pagado, y no se borra nada.',
    cta: 'Cancelar mi suscripción',
    confirmTitle: '¿Cancelar tu suscripción?',
    confirmBody: 'Tu plan y tus datos se quedan tal cual. Puedes volver a suscribirte cuando quieras.',
    reasonLabel: '¿Por qué cancelas? (opcional)',
    reasonPlaceholder: 'Lo que escribas aquí llega directamente al equipo.',
    confirm: 'Confirmar cancelación',
    keep: 'Mantener mi suscripción',
    working: 'Cancelando…',
    doneTitle: 'Tu suscripción está cancelada',
    doneBody: 'Gracias: ese comentario llega al equipo.',
    doneUntil: 'Mantienes el acceso completo hasta el {date}.',
    doneNoSub: 'No había ninguna suscripción recurrente activa en esta cuenta, así que no se cobrará nada más.',
    failed: 'No hemos podido cancelar la suscripción. No ha cambiado nada: escribe a {email} y lo resolvemos.',
  },
}

const pt: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Painel',
    checkoutReturned: 'Você voltou do checkout. Se já enviou um pagamento, confira o status da sua cobrança antes de tentar de novo.',
    sectionAria: 'Escolha o nível de ajuda certo',
    intro: 'Comece com uma pergunta financeira. Explore as opções suportadas, entenda os trade-offs e defina com o MyCIO próximos passos práticos. Os planos salvos permitem continuar a conversa conforme sua situação muda.',
    notAdviceBefore: 'O MyCIO ajuda você a planejar o que fazer; ele não executa operações nem movimenta seu dinheiro. Confira ',
    whatWeModel: 'o que modelamos',
    notAdviceMiddle: ' e ',
    seeExample: 'veja um exemplo',
    notAdviceAfter: ' antes de escolher um plano pago.',
    selectionBefore: 'Sua seleção: ',
    selectionAfter: '. Revise o preço e as condições antes de continuar; entrar na conta não inicia o checkout.',
    continueWith: 'Continuar com {plan}',
    titleBefore: 'Ajuda para a sua pergunta. ',
    titleEm: 'Um plano para o que vem a seguir.',
    titleAfter: '',
    body: 'Escolha quanta ajuda contínua você precisa. Todos os níveis têm limites de cobertura; uma compra não torna disponível um cenário não suportado.',
    renewalBody: 'Plus e Wealth incluem o primeiro ano do Living Plan. A renovação anual anunciada é de {plus} para o Plus e de {wealth} para o Wealth. Revise as condições de renovação antes de comprar e fale conosco para confirmar as condições de um trabalho Transfronteiriço.',
    footer: 'Os preços são exibidos em USD. Revise o total, a periodicidade de cobrança e os impostos aplicáveis exibidos no checkout antes de confirmar o pagamento.',
    questionsLink: 'Dúvidas sobre um plano ou a renovação?',
    privacyLink: 'Política de privacidade',
  },
  checkout: {
    unavailable: 'O checkout não está disponível agora — tente novamente em instantes.',
    betaConfirm: 'No momento você tem acesso beta completo.\n\nAo comprar agora, será cobrado o preço do primeiro ano exibido no checkout. No Plus ou no Wealth, a renovação anual mais baixa começa um ano após a compra; ela não depende de quando o acesso beta termina.\n\nContinuar para o checkout?',
  },
  pay: {
    opening: 'Abrindo o checkout seguro…',
    open: 'Conclua sua compra na janela de checkout seguro. Se ela não abriu, permita pop-ups para este site e recarregue a página.',
    returning: 'Voltando para os preços…',
    failedBefore: 'Não foi possível carregar o checkout. ',
    returnToPricing: 'Volte aos preços',
    failedAfter: ' e tente novamente.',
    noCheckoutBefore: 'Não há checkout para abrir. ',
    choosePlan: 'Escolha um plano',
    noCheckoutAfter: ' para iniciar um.',
    unavailableBefore: 'O checkout está temporariamente indisponível. Tente novamente em instantes ou ',
    contactSupport: 'fale com o suporte',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Cancelar assinatura',
    body: 'O cancelamento interrompe as renovações futuras. Você mantém acesso completo até o fim do período já pago, e nada é excluído.',
    cta: 'Cancelar minha assinatura',
    confirmTitle: 'Cancelar sua assinatura?',
    confirmBody: 'Seu plano e seus dados continuam exatamente como estão. Você pode assinar novamente quando quiser.',
    reasonLabel: 'Por que você está cancelando? (opcional)',
    reasonPlaceholder: 'O que você escrever aqui vai direto para a equipe.',
    confirm: 'Confirmar cancelamento',
    keep: 'Manter minha assinatura',
    working: 'Cancelando…',
    doneTitle: 'Sua assinatura foi cancelada',
    doneBody: 'Obrigado — esse comentário vai para a equipe.',
    doneUntil: 'Você mantém acesso completo até {date}.',
    doneNoSub: 'Não havia assinatura recorrente ativa nesta conta, então nada mais será cobrado.',
    failed: 'Não conseguimos cancelar a assinatura. Nada mudou — escreva para {email} e nós resolvemos.',
  },
}

const fr: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Tableau de bord',
    checkoutReturned: 'Vous êtes revenu du paiement. Si vous avez déjà validé un paiement, vérifiez l’état de votre facturation avant de réessayer.',
    sectionAria: 'Choisissez le bon niveau d’accompagnement',
    intro: 'Commencez par une question financière. Explorez les options prises en charge, comprenez leurs arbitrages et définissez avec MyCIO des prochaines étapes concrètes. Les plans enregistrés vous permettent de poursuivre l’échange à mesure que votre situation évolue.',
    notAdviceBefore: 'MyCIO vous aide à planifier ce que vous allez faire ; il ne passe pas d’ordres et ne déplace pas votre argent. Consultez ',
    whatWeModel: 'ce que nous modélisons',
    notAdviceMiddle: ' et ',
    seeExample: 'un exemple',
    notAdviceAfter: ' avant de choisir une formule payante.',
    selectionBefore: 'Votre sélection : ',
    selectionAfter: '. Vérifiez le prix et les conditions avant de continuer ; la connexion ne lance pas le paiement.',
    continueWith: 'Continuer avec {plan}',
    titleBefore: 'De l’aide pour votre question. ',
    titleEm: 'Un plan pour la suite.',
    titleAfter: '',
    body: 'Choisissez le niveau d’accompagnement continu dont vous avez besoin. Chaque formule a des limites de couverture ; un achat ne rend pas disponible un scénario non pris en charge.',
    renewalBody: 'Plus et Wealth incluent la première année de Living Plan. Le renouvellement annuel annoncé est de {plus} pour Plus et de {wealth} pour Wealth. Vérifiez les conditions de renouvellement avant l’achat, et contactez-nous pour confirmer les conditions d’une mission Transfrontalier.',
    footer: 'Les prix sont indiqués en USD. Vérifiez le total, la périodicité de facturation et les taxes applicables affichés lors du paiement avant de le confirmer.',
    questionsLink: 'Une question sur une formule ou un renouvellement ?',
    privacyLink: 'Politique de confidentialité',
  },
  checkout: {
    unavailable: 'Le paiement n’est pas disponible pour le moment — veuillez réessayer dans quelques instants.',
    betaConfirm: 'Vous disposez actuellement d’un accès bêta complet.\n\nUn achat maintenant débite le prix de la première année affiché lors du paiement. Pour Plus ou Wealth, le renouvellement annuel réduit commence un an après l’achat ; il ne dépend pas de la fin de l’accès bêta.\n\nContinuer vers le paiement ?',
  },
  pay: {
    opening: 'Ouverture du paiement sécurisé…',
    open: 'Finalisez votre achat dans la fenêtre de paiement sécurisé. Si elle ne s’est pas ouverte, autorisez les fenêtres pop-up pour ce site et rechargez la page.',
    returning: 'Retour aux tarifs…',
    failedBefore: 'Le paiement n’a pas pu être chargé. ',
    returnToPricing: 'Revenez aux tarifs',
    failedAfter: ' et réessayez.',
    noCheckoutBefore: 'Aucun paiement à ouvrir. ',
    choosePlan: 'Choisissez une formule',
    noCheckoutAfter: ' pour en lancer un.',
    unavailableBefore: 'Le paiement est temporairement indisponible. Réessayez dans quelques instants ou ',
    contactSupport: 'contactez le support',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Résilier l’abonnement',
    body: 'La résiliation arrête les renouvellements futurs. Vous conservez l’accès complet jusqu’à la fin de la période déjà payée, et rien n’est supprimé.',
    cta: 'Résilier mon abonnement',
    confirmTitle: 'Résilier votre abonnement ?',
    confirmBody: 'Votre plan et vos données restent intacts. Vous pouvez vous réabonner à tout moment.',
    reasonLabel: 'Pourquoi résiliez-vous ? (facultatif)',
    reasonPlaceholder: 'Ce que vous écrivez ici va directement à l’équipe.',
    confirm: 'Confirmer la résiliation',
    keep: 'Conserver mon abonnement',
    working: 'Résiliation…',
    doneTitle: 'Votre abonnement est résilié',
    doneBody: 'Merci — ce retour est transmis à l’équipe.',
    doneUntil: 'Vous gardez l’accès complet jusqu’au {date}.',
    doneNoSub: 'Aucun abonnement récurrent n’était actif sur ce compte : plus rien ne sera débité.',
    failed: 'Nous n’avons pas pu résilier l’abonnement. Rien n’a changé — écrivez à {email} et nous nous en occupons.',
  },
}

const de: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Übersicht',
    checkoutReturned: 'Sie sind vom Bezahlvorgang zurückgekehrt. Wenn Sie bereits eine Zahlung abgeschickt haben, prüfen Sie Ihren Abrechnungsstatus, bevor Sie es erneut versuchen.',
    sectionAria: 'Wählen Sie den passenden Umfang an Unterstützung',
    intro: 'Beginnen Sie mit einer Finanzfrage. Erkunden Sie unterstützte Optionen, verstehen Sie die Zielkonflikte und erarbeiten Sie mit MyCIO konkrete nächste Schritte. Mit gespeicherten Plänen können Sie das Gespräch fortsetzen, wenn sich Ihre Situation ändert.',
    notAdviceBefore: 'MyCIO hilft Ihnen zu planen, was Sie tun; es führt keine Transaktionen aus und bewegt kein Geld. Sehen Sie sich an, ',
    whatWeModel: 'was wir modellieren',
    notAdviceMiddle: ', und ',
    seeExample: 'ein Beispiel',
    notAdviceAfter: ', bevor Sie einen kostenpflichtigen Plan wählen.',
    selectionBefore: 'Ihre Auswahl: ',
    selectionAfter: '. Prüfen Sie Preis und Bedingungen, bevor Sie fortfahren; die Anmeldung startet keinen Bezahlvorgang.',
    continueWith: 'Weiter mit {plan}',
    titleBefore: 'Hilfe für Ihre Frage. ',
    titleEm: 'Ein Plan für das, was kommt.',
    titleAfter: '',
    body: 'Wählen Sie, wie viel laufende Unterstützung Sie brauchen. Für jede Stufe gelten Grenzen des Abdeckungsbereichs; ein Kauf macht ein nicht unterstütztes Szenario nicht verfügbar.',
    renewalBody: 'Plus und Wealth enthalten das erste Jahr Living Plan. Die ausgewiesene jährliche Verlängerung beträgt {plus} für Plus und {wealth} für Wealth. Prüfen Sie die Verlängerungsbedingungen vor dem Kauf und stimmen Sie die Bedingungen für ein grenzüberschreitendes Mandat vorab mit uns ab.',
    footer: 'Die Preise sind in USD angegeben. Prüfen Sie vor der Bestätigung der Zahlung den Gesamtbetrag, den Abrechnungszeitraum und etwaige Steuern, die im Bezahlvorgang angezeigt werden.',
    questionsLink: 'Fragen zu einem Plan oder zur Verlängerung?',
    privacyLink: 'Datenschutzerklärung',
  },
  checkout: {
    unavailable: 'Der Bezahlvorgang ist gerade nicht verfügbar – bitte versuchen Sie es in Kürze erneut.',
    betaConfirm: 'Sie haben derzeit vollen Beta-Zugang.\n\nBei einem Kauf jetzt wird der im Bezahlvorgang angezeigte Preis für das erste Jahr berechnet. Bei Plus oder Wealth beginnt die günstigere jährliche Verlängerung ein Jahr nach dem Kauf; sie hängt nicht davon ab, wann der Beta-Zugang endet.\n\nWeiter zum Bezahlvorgang?',
  },
  pay: {
    opening: 'Sicherer Bezahlvorgang wird geöffnet…',
    open: 'Schließen Sie Ihren Kauf im sicheren Bezahlfenster ab. Falls es sich nicht geöffnet hat, erlauben Sie Pop-ups für diese Website und laden Sie die Seite neu.',
    returning: 'Zurück zu den Preisen…',
    failedBefore: 'Der Bezahlvorgang konnte nicht geladen werden. ',
    returnToPricing: 'Kehren Sie zu den Preisen zurück',
    failedAfter: ' und versuchen Sie es erneut.',
    noCheckoutBefore: 'Es gibt keinen Bezahlvorgang zum Öffnen. ',
    choosePlan: 'Wählen Sie einen Plan',
    noCheckoutAfter: ', um einen zu starten.',
    unavailableBefore: 'Der Bezahlvorgang ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut oder ',
    contactSupport: 'wenden Sie sich an den Support',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Abonnement kündigen',
    body: 'Die Kündigung stoppt künftige Verlängerungen. Sie behalten den vollen Zugang bis zum Ende des bereits bezahlten Zeitraums, und es wird nichts gelöscht.',
    cta: 'Abonnement kündigen',
    confirmTitle: 'Abonnement wirklich kündigen?',
    confirmBody: 'Ihr Plan und Ihre Daten bleiben unverändert. Sie können jederzeit erneut abonnieren.',
    reasonLabel: 'Warum kündigen Sie? (optional)',
    reasonPlaceholder: 'Was Sie hier schreiben, geht direkt an das Team.',
    confirm: 'Kündigung bestätigen',
    keep: 'Abonnement behalten',
    working: 'Wird gekündigt…',
    doneTitle: 'Ihr Abonnement ist gekündigt',
    doneBody: 'Danke — dieses Feedback geht an das Team.',
    doneUntil: 'Sie behalten den vollen Zugang bis zum {date}.',
    doneNoSub: 'Auf diesem Konto war kein laufendes Abonnement aktiv, es wird nichts weiter berechnet.',
    failed: 'Wir konnten das Abonnement nicht kündigen. Es hat sich nichts geändert — schreiben Sie an {email}.',
  },
}

const it: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Pannello',
    checkoutReturned: 'Sei tornato dal pagamento. Se hai già inviato un pagamento, controlla lo stato della fatturazione prima di riprovare.',
    sectionAria: 'Scegli il livello di aiuto adatto',
    intro: 'Parti da una domanda finanziaria. Esplora le opzioni supportate, comprendi i compromessi e definisci con MyCIO passi successivi concreti. I piani salvati ti permettono di proseguire la conversazione man mano che la tua situazione cambia.',
    notAdviceBefore: 'MyCIO ti aiuta a pianificare cosa fare; non esegue operazioni e non sposta il tuo denaro. Consulta ',
    whatWeModel: 'cosa modelliamo',
    notAdviceMiddle: ' e ',
    seeExample: 'guarda un esempio',
    notAdviceAfter: ' prima di scegliere un piano a pagamento.',
    selectionBefore: 'La tua scelta: ',
    selectionAfter: '. Verifica prezzo e condizioni prima di continuare; l’accesso non avvia il pagamento.',
    continueWith: 'Continua con {plan}',
    titleBefore: 'Aiuto per la tua domanda. ',
    titleEm: 'Un piano per ciò che verrà.',
    titleAfter: '',
    body: 'Scegli quanto aiuto continuativo ti serve. Ogni livello ha limiti di copertura; un acquisto non rende disponibile uno scenario non supportato.',
    renewalBody: 'Plus e Wealth includono il primo anno di Living Plan. Il rinnovo annuale indicato è di {plus} per Plus e di {wealth} per Wealth. Verifica le condizioni di rinnovo prima dell’acquisto e contattaci per confermare le condizioni di un incarico Transfrontaliero.',
    footer: 'I prezzi sono indicati in USD. Prima di confermare il pagamento, verifica il totale, la periodicità di fatturazione ed eventuali imposte mostrati al momento del pagamento.',
    questionsLink: 'Domande su un piano o sul rinnovo?',
    privacyLink: 'Informativa sulla privacy',
  },
  checkout: {
    unavailable: 'Il pagamento non è disponibile in questo momento: riprova tra poco.',
    betaConfirm: 'Al momento hai l’accesso beta completo.\n\nAcquistando ora ti viene addebitato il prezzo del primo anno mostrato al pagamento. Per Plus o Wealth, il rinnovo annuale più basso inizia un anno dopo l’acquisto; non dipende dalla fine dell’accesso beta.\n\nProcedere al pagamento?',
  },
  pay: {
    opening: 'Apertura del pagamento sicuro…',
    open: 'Completa l’acquisto nella finestra di pagamento sicuro. Se non si è aperta, consenti i pop-up per questo sito e ricarica la pagina.',
    returning: 'Ritorno ai prezzi…',
    failedBefore: 'Non è stato possibile caricare il pagamento. ',
    returnToPricing: 'Torna ai prezzi',
    failedAfter: ' e riprova.',
    noCheckoutBefore: 'Non c’è alcun pagamento da aprire. ',
    choosePlan: 'Scegli un piano',
    noCheckoutAfter: ' per avviarne uno.',
    unavailableBefore: 'Il pagamento è temporaneamente non disponibile. Riprova tra poco oppure ',
    contactSupport: 'contatta l’assistenza',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Annulla abbonamento',
    body: 'L’annullamento interrompe i rinnovi futuri. Mantieni l’accesso completo fino alla fine del periodo già pagato e non viene cancellato nulla.',
    cta: 'Annulla il mio abbonamento',
    confirmTitle: 'Vuoi annullare l’abbonamento?',
    confirmBody: 'Il tuo piano e i tuoi dati restano esattamente come sono. Puoi abbonarti di nuovo in qualsiasi momento.',
    reasonLabel: 'Perché annulli? (facoltativo)',
    reasonPlaceholder: 'Quello che scrivi qui arriva direttamente al team.',
    confirm: 'Conferma l’annullamento',
    keep: 'Mantieni l’abbonamento',
    working: 'Annullamento in corso…',
    doneTitle: 'Il tuo abbonamento è stato annullato',
    doneBody: 'Grazie: il tuo commento arriva al team.',
    doneUntil: 'Mantieni l’accesso completo fino al {date}.',
    doneNoSub: 'Su questo account non c’era alcun abbonamento ricorrente attivo, quindi non verrà addebitato altro.',
    failed: 'Non siamo riusciti ad annullare l’abbonamento. Non è cambiato nulla: scrivi a {email} e ce ne occupiamo noi.',
  },
}

const ru: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Панель',
    checkoutReturned: 'Вы вернулись со страницы оплаты. Если платёж уже был отправлен, проверьте статус оплаты, прежде чем пробовать снова.',
    sectionAria: 'Выберите подходящий уровень поддержки',
    intro: 'Начните с финансового вопроса. Изучите поддерживаемые варианты, разберитесь в компромиссах и наметьте вместе с MyCIO практические следующие шаги. Сохранённые планы позволяют продолжить разговор, когда меняется ваша ситуация.',
    notAdviceBefore: 'MyCIO помогает спланировать, что делать; он не совершает сделок и не перемещает ваши деньги. Прежде чем выбрать платный план, посмотрите, ',
    whatWeModel: 'что мы моделируем',
    notAdviceMiddle: ', и ',
    seeExample: 'пример',
    notAdviceAfter: '.',
    selectionBefore: 'Ваш выбор: ',
    selectionAfter: '. Проверьте цену и условия, прежде чем продолжить; вход в аккаунт не запускает оплату.',
    continueWith: 'Продолжить с {plan}',
    titleBefore: 'Помощь с вашим вопросом. ',
    titleEm: 'План на то, что впереди.',
    titleAfter: '',
    body: 'Выберите, сколько постоянной поддержки вам нужно. У каждого уровня есть ограничения охвата; покупка не делает доступным неподдерживаемый сценарий.',
    renewalBody: 'Plus и Wealth включают первый год Living Plan. Объявленная стоимость ежегодного продления — {plus} для Plus и {wealth} для Wealth. Перед покупкой ознакомьтесь с условиями продления, а условия трансграничного сопровождения согласуйте с нами.',
    footer: 'Цены указаны в долларах США. Перед подтверждением платежа проверьте итоговую сумму, периодичность оплаты и применимые налоги, показанные при оформлении.',
    questionsLink: 'Вопросы о плане или продлении?',
    privacyLink: 'Политика конфиденциальности',
  },
  checkout: {
    unavailable: 'Оплата сейчас недоступна — попробуйте ещё раз чуть позже.',
    betaConfirm: 'Сейчас у вас полный бета-доступ.\n\nПри покупке сейчас списывается цена первого года, показанная при оформлении. Для Plus или Wealth более низкое ежегодное продление начинается через год после покупки и не зависит от того, когда закончится бета-доступ.\n\nПерейти к оплате?',
  },
  pay: {
    opening: 'Открываем защищённую оплату…',
    open: 'Завершите покупку в окне защищённой оплаты. Если оно не открылось, разрешите всплывающие окна для этого сайта и перезагрузите страницу.',
    returning: 'Возвращаем вас к ценам…',
    failedBefore: 'Не удалось загрузить оплату. ',
    returnToPricing: 'Вернитесь к ценам',
    failedAfter: ' и попробуйте снова.',
    noCheckoutBefore: 'Открывать нечего: оплата не начата. ',
    choosePlan: 'Выберите план',
    noCheckoutAfter: ', чтобы начать её.',
    unavailableBefore: 'Оплата временно недоступна. Попробуйте чуть позже или ',
    contactSupport: 'обратитесь в поддержку',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Отменить подписку',
    body: 'Отмена прекращает будущие продления. Полный доступ сохраняется до конца уже оплаченного периода, и ничего не удаляется.',
    cta: 'Отменить мою подписку',
    confirmTitle: 'Отменить подписку?',
    confirmBody: 'Ваш план и данные останутся без изменений. Оформить подписку снова можно в любой момент.',
    reasonLabel: 'Почему вы отменяете подписку? (необязательно)',
    reasonPlaceholder: 'Всё, что вы здесь напишете, попадёт прямо к команде.',
    confirm: 'Подтвердить отмену',
    keep: 'Сохранить подписку',
    working: 'Отменяем…',
    doneTitle: 'Подписка отменена',
    doneBody: 'Спасибо — ваш отзыв передан команде.',
    doneUntil: 'Полный доступ сохраняется до {date}.',
    doneNoSub: 'На этом аккаунте не было активной регулярной подписки, поэтому больше ничего списано не будет.',
    failed: 'Не удалось отменить подписку. Ничего не изменилось — напишите на {email}, и мы всё сделаем.',
  },
}

const zh: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← 控制面板',
    checkoutReturned: '你已从结账页面返回。如果已经提交过付款，请先查看账单状态再重试。',
    sectionAria: '选择合适的帮助级别',
    intro: '从一个财务问题开始。与 MyCIO 一起探索受支持的选项、理解其中的取舍，并确定切实可行的下一步。保存的计划可让你在情况变化时继续这段对话。',
    notAdviceBefore: 'MyCIO 帮助你规划要做什么；它不进行交易，也不转移你的资金。选择付费计划前，请先查看',
    whatWeModel: '我们的建模范围',
    notAdviceMiddle: '并',
    seeExample: '看一个示例',
    notAdviceAfter: '。',
    selectionBefore: '你的选择：',
    selectionAfter: '。继续前请核对价格和条款；登录不会开始结账。',
    continueWith: '继续选择 {plan}',
    titleBefore: '为你的问题提供帮助。',
    titleEm: '为接下来的事做好计划。',
    titleAfter: '',
    body: '选择你需要多少持续帮助。每个级别都有覆盖范围限制；购买不会让不受支持的情景变为可用。',
    renewalBody: 'Plus 和 Wealth 包含首年 Living Plan。公布的年度续费为 Plus {plus}、Wealth {wealth}。购买前请查看续费条款；跨境服务的条款请与我们联系确认。',
    footer: '价格以美元显示。确认付款前，请核对结账时显示的总额、计费周期及适用税费。',
    questionsLink: '对计划或续费有疑问？',
    privacyLink: '隐私政策',
  },
  checkout: {
    unavailable: '目前无法结账，请稍后再试。',
    betaConfirm: '你目前拥有完整的测试版访问权限。\n\n现在购买将按结账时显示的首年价格收费。对于 Plus 或 Wealth，较低的年度续费从购买一年后开始，与测试版访问何时结束无关。\n\n继续结账？',
  },
  pay: {
    opening: '正在打开安全结账…',
    open: '请在安全结账窗口中完成购买。如果窗口没有打开，请允许本网站弹出窗口并重新加载页面。',
    returning: '正在返回价格页面…',
    failedBefore: '无法加载结账页面。',
    returnToPricing: '返回价格页面',
    failedAfter: '后重试。',
    noCheckoutBefore: '没有可打开的结账。',
    choosePlan: '选择一个计划',
    noCheckoutAfter: '即可开始。',
    unavailableBefore: '结账暂时不可用。请稍后再试，或',
    contactSupport: '联系支持团队',
    unavailableAfter: '。',
  },
  cancel: {
    title: '取消订阅',
    body: '取消后将不再续费。在已付费期间结束前，你仍可完整使用，且不会删除任何内容。',
    cta: '取消我的订阅',
    confirmTitle: '要取消订阅吗？',
    confirmBody: '你的计划和数据将保持原样。你可以随时重新订阅。',
    reasonLabel: '取消的原因是什么？（可选）',
    reasonPlaceholder: '你在这里写的内容会直接送达团队。',
    confirm: '确认取消',
    keep: '保留我的订阅',
    working: '正在取消…',
    doneTitle: '你的订阅已取消',
    doneBody: '谢谢，你的反馈已转给团队。',
    doneUntil: '在 {date} 之前你仍可完整使用。',
    doneNoSub: '此账户没有有效的定期订阅，因此不会再产生任何费用。',
    failed: '我们未能取消订阅。一切保持不变——请发送邮件至 {email}，我们会为你处理。',
  },
}

const ja: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← ダッシュボード',
    checkoutReturned: '決済画面から戻りました。すでにお支払いを送信した場合は、再度お試しになる前に請求状況をご確認ください。',
    sectionAria: '必要なサポートの範囲を選ぶ',
    intro: 'お金に関する質問から始めましょう。MyCIO と一緒に、対応している選択肢を検討し、トレードオフを理解して、実行可能な次のステップを考えます。保存したプランがあれば、状況が変わっても会話を続けられます。',
    notAdviceBefore: 'MyCIO は何をするかの計画をお手伝いしますが、売買の執行や資金の移動は行いません。有料プランを選ぶ前に、',
    whatWeModel: 'モデル化の対象',
    notAdviceMiddle: 'と',
    seeExample: '例',
    notAdviceAfter: 'をご確認ください。',
    selectionBefore: '選択中のプラン：',
    selectionAfter: '。続行する前に価格と条件をご確認ください。ログインしても決済は始まりません。',
    continueWith: '{plan} で続ける',
    titleBefore: 'その質問に、手助けを。',
    titleEm: 'これからのための計画を。',
    titleAfter: '',
    body: '必要な継続サポートの量をお選びください。どのプランにも対応範囲の制限があり、購入しても対応外のシナリオが利用可能になるわけではありません。',
    renewalBody: 'Plus と Wealth には Living Plan の初年度が含まれます。案内している年間更新料は Plus が {plus}、Wealth が {wealth} です。購入前に更新条件をご確認ください。クロスボーダーのご依頼は、条件をお問い合わせのうえご確認ください。',
    footer: '価格は米ドルで表示しています。お支払いを確定する前に、決済画面に表示される合計額、請求間隔、適用される税金をご確認ください。',
    questionsLink: 'プランや更新についてのご質問はこちら',
    privacyLink: 'プライバシーポリシー',
  },
  checkout: {
    unavailable: '現在、決済をご利用いただけません。しばらくしてから再度お試しください。',
    betaConfirm: '現在、ベータ版のフルアクセスをご利用中です。\n\n今購入すると、決済画面に表示される初年度の価格が請求されます。Plus または Wealth の場合、割安な年間更新は購入から1年後に始まり、ベータアクセスの終了時期とは関係ありません。\n\n決済に進みますか？',
  },
  pay: {
    opening: '安全な決済画面を開いています…',
    open: '安全な決済ウィンドウで購入を完了してください。開かない場合は、このサイトのポップアップを許可してページを再読み込みしてください。',
    returning: '料金ページに戻っています…',
    failedBefore: '決済画面を読み込めませんでした。',
    returnToPricing: '料金ページに戻って',
    failedAfter: '、もう一度お試しください。',
    noCheckoutBefore: '開く決済がありません。',
    choosePlan: 'プランを選ぶ',
    noCheckoutAfter: 'と決済を始められます。',
    unavailableBefore: '決済は一時的にご利用いただけません。しばらくしてから再度お試しいただくか、',
    contactSupport: 'サポートにお問い合わせ',
    unavailableAfter: 'ください。',
  },
  cancel: {
    title: 'サブスクリプションの解約',
    body: '解約すると以降の更新は停止します。お支払い済みの期間が終わるまではすべての機能をご利用いただけ、データは削除されません。',
    cta: 'サブスクリプションを解約する',
    confirmTitle: 'サブスクリプションを解約しますか？',
    confirmBody: 'プランとデータはそのまま残ります。いつでも再度ご契約いただけます。',
    reasonLabel: '解約の理由をお聞かせください（任意）',
    reasonPlaceholder: 'ここにご記入いただいた内容は、そのままチームに届きます。',
    confirm: '解約を確定する',
    keep: 'サブスクリプションを続ける',
    working: '解約しています…',
    doneTitle: 'サブスクリプションを解約しました',
    doneBody: 'ありがとうございます。ご意見はチームに届けます。',
    doneUntil: '{date} まではすべての機能をご利用いただけます。',
    doneNoSub: 'このアカウントには有効な定期サブスクリプションがなかったため、今後の請求はありません。',
    failed: 'サブスクリプションを解約できませんでした。変更は何も行われていません。{email} までメールでご連絡いただければ、こちらで対応いたします。',
  },
}

const ko: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← 대시보드',
    checkoutReturned: '결제 화면에서 돌아왔습니다. 이미 결제를 제출했다면 다시 시도하기 전에 결제 상태를 확인하세요.',
    sectionAria: '알맞은 도움 수준 선택',
    intro: '재무 질문 하나로 시작하세요. MyCIO와 함께 지원되는 선택지를 살펴보고, 장단점을 이해하고, 실천할 수 있는 다음 단계를 정리하세요. 계획을 저장해 두면 상황이 바뀔 때 대화를 이어갈 수 있습니다.',
    notAdviceBefore: 'MyCIO는 무엇을 할지 계획하도록 돕지만, 매매를 실행하거나 자금을 이동하지 않습니다. 유료 플랜을 선택하기 전에 ',
    whatWeModel: '모델링 범위',
    notAdviceMiddle: '와 ',
    seeExample: '예시',
    notAdviceAfter: '를 확인하세요.',
    selectionBefore: '선택한 플랜: ',
    selectionAfter: '. 계속하기 전에 가격과 조건을 확인하세요. 로그인해도 결제가 시작되지 않습니다.',
    continueWith: '{plan}(으)로 계속',
    titleBefore: '질문에는 도움을. ',
    titleEm: '앞으로를 위한 계획을.',
    titleAfter: '',
    body: '필요한 지속 지원의 수준을 선택하세요. 모든 등급에 지원 범위 제한이 있으며, 구매한다고 해서 지원되지 않는 시나리오를 이용할 수 있게 되지는 않습니다.',
    renewalBody: 'Plus와 Wealth에는 Living Plan 첫해가 포함됩니다. 안내된 연간 갱신 비용은 Plus {plus}, Wealth {wealth}입니다. 구매 전에 갱신 조건을 확인하시고, 국경 간 계획 의뢰는 저희에게 문의해 조건을 확인하세요.',
    footer: '가격은 미국 달러로 표시됩니다. 결제를 확정하기 전에 결제 화면에 표시되는 총액, 결제 주기, 적용 세금을 확인하세요.',
    questionsLink: '플랜이나 갱신에 대해 궁금하신가요?',
    privacyLink: '개인정보 처리방침',
  },
  checkout: {
    unavailable: '지금은 결제를 이용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
    betaConfirm: '현재 전체 베타 이용 권한이 있습니다.\n\n지금 구매하면 결제 화면에 표시된 첫해 가격이 청구됩니다. Plus 또는 Wealth의 경우 더 낮은 연간 갱신은 구매 1년 후부터 시작되며, 베타 이용 종료 시점과는 관계없습니다.\n\n결제를 계속하시겠습니까?',
  },
  pay: {
    opening: '보안 결제 화면을 여는 중…',
    open: '보안 결제 창에서 구매를 완료하세요. 창이 열리지 않았다면 이 사이트의 팝업을 허용하고 새로 고침하세요.',
    returning: '가격 페이지로 돌아가는 중…',
    failedBefore: '결제 화면을 불러오지 못했습니다. ',
    returnToPricing: '가격 페이지로 돌아가',
    failedAfter: ' 다시 시도하세요.',
    noCheckoutBefore: '열 수 있는 결제가 없습니다. ',
    choosePlan: '플랜을 선택',
    noCheckoutAfter: '해 결제를 시작하세요.',
    unavailableBefore: '결제를 일시적으로 이용할 수 없습니다. 잠시 후 다시 시도하거나 ',
    contactSupport: '고객 지원에 문의',
    unavailableAfter: '하세요.',
  },
  cancel: {
    title: '구독 취소',
    body: '취소하면 이후 갱신이 중단됩니다. 이미 결제한 기간이 끝날 때까지 모든 기능을 이용할 수 있으며, 아무것도 삭제되지 않습니다.',
    cta: '내 구독 취소',
    confirmTitle: '구독을 취소하시겠습니까?',
    confirmBody: '계획과 데이터는 그대로 유지됩니다. 언제든 다시 구독할 수 있습니다.',
    reasonLabel: '취소하시는 이유는 무엇인가요? (선택)',
    reasonPlaceholder: '여기에 적어 주신 내용은 팀에 바로 전달됩니다.',
    confirm: '취소 확정',
    keep: '구독 유지',
    working: '취소하는 중…',
    doneTitle: '구독이 취소되었습니다',
    doneBody: '감사합니다. 의견은 팀에 전달됩니다.',
    doneUntil: '{date}까지 모든 기능을 이용할 수 있습니다.',
    doneNoSub: '이 계정에는 활성 정기 구독이 없었으므로 더 이상 청구되지 않습니다.',
    failed: '구독을 취소하지 못했습니다. 변경된 것은 없습니다. {email}로 이메일을 보내 주시면 처리해 드리겠습니다.',
  },
}

const ar: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '→ لوحة التحكم',
    checkoutReturned: 'لقد عدت من صفحة الدفع. إذا كنت قد أرسلت دفعة بالفعل، فراجِع حالة الفوترة قبل المحاولة مجددًا.',
    sectionAria: 'اختر مستوى المساعدة المناسب',
    intro: 'ابدأ بسؤال مالي. استكشف الخيارات المدعومة، وافهم المفاضلات بينها، وحدّد مع MyCIO خطوات تالية عملية. تتيح لك الخطط المحفوظة مواصلة الحديث كلما تغيّر وضعك.',
    notAdviceBefore: 'يساعدك MyCIO على التخطيط لما ستفعله؛ فهو لا ينفّذ صفقات ولا ينقل أموالك. اطّلع على ',
    whatWeModel: 'ما نقوم بنمذجته',
    notAdviceMiddle: ' و',
    seeExample: 'مثال توضيحي',
    notAdviceAfter: ' قبل اختيار خطة مدفوعة.',
    selectionBefore: 'اختيارك: ',
    selectionAfter: '. راجِع السعر والشروط قبل المتابعة؛ تسجيل الدخول لا يبدأ عملية الدفع.',
    continueWith: 'المتابعة مع {plan}',
    titleBefore: 'مساعدة في سؤالك. ',
    titleEm: 'وخطة لما هو آتٍ.',
    titleAfter: '',
    body: 'اختر مقدار المساعدة المستمرة التي تحتاجها. لكل مستوى حدود تغطية؛ والشراء لا يجعل سيناريو غير مدعوم متاحًا.',
    renewalBody: 'تشمل Plus وWealth السنة الأولى من Living Plan. التجديد السنوي المعلن هو {plus} لـ Plus و{wealth} لـ Wealth. راجِع شروط التجديد قبل الشراء، وتواصل معنا لتأكيد شروط أي تكليف عابر للحدود.',
    footer: 'الأسعار معروضة بالدولار الأمريكي. راجِع الإجمالي ودورية الفوترة وأي ضرائب مطبّقة تظهر عند الدفع قبل تأكيد الدفع.',
    questionsLink: 'أسئلة حول خطة أو تجديد؟',
    privacyLink: 'سياسة الخصوصية',
  },
  checkout: {
    unavailable: 'الدفع غير متاح الآن — يُرجى المحاولة مجددًا بعد قليل.',
    betaConfirm: 'لديك حاليًا وصول تجريبي كامل.\n\nالشراء الآن يُحتسب بسعر السنة الأولى المعروض عند الدفع. في Plus أو Wealth يبدأ التجديد السنوي الأقل بعد عام من الشراء، ولا يرتبط بموعد انتهاء الوصول التجريبي.\n\nالمتابعة إلى الدفع؟',
  },
  pay: {
    opening: 'جارٍ فتح صفحة الدفع الآمن…',
    open: 'أكمِل عملية الشراء في نافذة الدفع الآمن. إن لم تُفتح، فاسمح بالنوافذ المنبثقة لهذا الموقع ثم أعد تحميل الصفحة.',
    returning: 'جارٍ إعادتك إلى صفحة الأسعار…',
    failedBefore: 'تعذّر تحميل صفحة الدفع. ',
    returnToPricing: 'عُد إلى صفحة الأسعار',
    failedAfter: ' وحاول مجددًا.',
    noCheckoutBefore: 'لا توجد عملية دفع لفتحها. ',
    choosePlan: 'اختر خطة',
    noCheckoutAfter: ' لبدء واحدة.',
    unavailableBefore: 'الدفع غير متاح مؤقتًا. يُرجى المحاولة بعد قليل أو ',
    contactSupport: 'التواصل مع الدعم',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'إلغاء الاشتراك',
    body: 'يوقف الإلغاء التجديدات المستقبلية. يبقى لديك وصول كامل حتى نهاية الفترة التي دفعت ثمنها، ولا يُحذف أي شيء.',
    cta: 'إلغاء اشتراكي',
    confirmTitle: 'هل تريد إلغاء اشتراكك؟',
    confirmBody: 'تبقى خطتك وبياناتك كما هي تمامًا. ويمكنك الاشتراك مجددًا في أي وقت.',
    reasonLabel: 'ما سبب الإلغاء؟ (اختياري)',
    reasonPlaceholder: 'كل ما تكتبه هنا يصل مباشرة إلى الفريق.',
    confirm: 'تأكيد الإلغاء',
    keep: 'الإبقاء على اشتراكي',
    working: 'جارٍ الإلغاء…',
    doneTitle: 'تم إلغاء اشتراكك',
    doneBody: 'شكرًا لك — ستصل ملاحظاتك إلى الفريق.',
    doneUntil: 'يبقى لديك وصول كامل حتى {date}.',
    doneNoSub: 'لم يكن على هذا الحساب أي اشتراك متكرر نشط، لذا لن تُحتسب أي رسوم أخرى.',
    failed: 'تعذّر علينا إلغاء الاشتراك. لم يتغيّر شيء — راسلنا على {email} وسنتولّى الأمر.',
  },
}

const hi: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← डैशबोर्ड',
    checkoutReturned: 'आप चेकआउट से लौट आए हैं। अगर आप भुगतान भेज चुके हैं, तो दोबारा कोशिश करने से पहले अपनी बिलिंग की स्थिति देख लें।',
    sectionAria: 'सही स्तर की मदद चुनें',
    intro: 'एक वित्तीय सवाल से शुरू करें। MyCIO के साथ समर्थित विकल्पों को खंगालें, उनके फ़ायदे-नुकसान समझें और व्यावहारिक अगले कदम तय करें। सहेजी गई योजनाओं से आप अपनी स्थिति बदलने पर बातचीत जारी रख सकते हैं।',
    notAdviceBefore: 'MyCIO आपको यह योजना बनाने में मदद करता है कि क्या करना है; यह ट्रेड नहीं करता और आपका पैसा नहीं भेजता। कोई पेड योजना चुनने से पहले ',
    whatWeModel: 'हम क्या मॉडल करते हैं',
    notAdviceMiddle: ' देखें और ',
    seeExample: 'एक उदाहरण देखें',
    notAdviceAfter: '।',
    selectionBefore: 'आपका चुनाव: ',
    selectionAfter: '। आगे बढ़ने से पहले कीमत और शर्तें देख लें; साइन इन करने से चेकआउट शुरू नहीं होता।',
    continueWith: '{plan} के साथ आगे बढ़ें',
    titleBefore: 'आपके सवाल के लिए मदद। ',
    titleEm: 'आगे के लिए एक योजना।',
    titleAfter: '',
    body: 'चुनें कि आपको कितनी निरंतर मदद चाहिए। हर स्तर पर कवरेज की सीमाएँ लागू हैं; खरीदने से कोई असमर्थित परिदृश्य उपलब्ध नहीं हो जाता।',
    renewalBody: 'Plus और Wealth में Living Plan का पहला साल शामिल है। घोषित सालाना नवीनीकरण Plus के लिए {plus} और Wealth के लिए {wealth} है। खरीदने से पहले नवीनीकरण की शर्तें देख लें, और क्रॉस-बॉर्डर काम की शर्तें पक्की करने के लिए हमसे संपर्क करें।',
    footer: 'कीमतें USD में दिखाई गई हैं। भुगतान की पुष्टि करने से पहले चेकआउट पर दिखाई गई कुल राशि, बिलिंग अवधि और लागू टैक्स देख लें।',
    questionsLink: 'किसी योजना या नवीनीकरण के बारे में सवाल?',
    privacyLink: 'प्राइवेसी पॉलिसी',
  },
  checkout: {
    unavailable: 'चेकआउट अभी उपलब्ध नहीं है — कृपया थोड़ी देर में फिर कोशिश करें।',
    betaConfirm: 'अभी आपके पास पूरा बीटा एक्सेस है।\n\nअभी खरीदने पर चेकआउट में दिखाई गई पहले साल की कीमत ली जाएगी। Plus या Wealth के लिए कम सालाना नवीनीकरण खरीद के एक साल बाद शुरू होता है; इसका बीटा एक्सेस खत्म होने के समय से कोई संबंध नहीं है।\n\nचेकआउट पर आगे बढ़ें?',
  },
  pay: {
    opening: 'सुरक्षित चेकआउट खुल रहा है…',
    open: 'सुरक्षित चेकआउट विंडो में अपनी खरीद पूरी करें। अगर वह नहीं खुली, तो इस साइट के लिए पॉप-अप की अनुमति दें और पेज दोबारा लोड करें।',
    returning: 'आपको कीमतों के पेज पर वापस ले जा रहे हैं…',
    failedBefore: 'चेकआउट लोड नहीं हो सका। ',
    returnToPricing: 'कीमतों के पेज पर लौटें',
    failedAfter: ' और फिर कोशिश करें।',
    noCheckoutBefore: 'खोलने के लिए कोई चेकआउट नहीं है। ',
    choosePlan: 'एक योजना चुनें',
    noCheckoutAfter: ' और शुरू करें।',
    unavailableBefore: 'चेकआउट अभी अस्थायी रूप से उपलब्ध नहीं है। कृपया थोड़ी देर में फिर कोशिश करें या ',
    contactSupport: 'सपोर्ट से संपर्क करें',
    unavailableAfter: '।',
  },
  cancel: {
    title: 'सब्सक्रिप्शन रद्द करें',
    body: 'रद्द करने से आगे के नवीनीकरण रुक जाते हैं। जिस अवधि का भुगतान आप कर चुके हैं, उसके अंत तक आपको पूरा एक्सेस मिलता रहेगा, और कुछ भी हटाया नहीं जाता।',
    cta: 'मेरा सब्सक्रिप्शन रद्द करें',
    confirmTitle: 'अपना सब्सक्रिप्शन रद्द करें?',
    confirmBody: 'आपकी योजना और डेटा जैसे हैं वैसे ही रहेंगे। आप कभी भी फिर से सब्सक्राइब कर सकते हैं।',
    reasonLabel: 'आप क्यों रद्द कर रहे हैं? (वैकल्पिक)',
    reasonPlaceholder: 'यहाँ आप जो भी लिखेंगे, वह सीधे टीम तक पहुँचेगा।',
    confirm: 'रद्द करने की पुष्टि करें',
    keep: 'मेरा सब्सक्रिप्शन जारी रखें',
    working: 'रद्द किया जा रहा है…',
    doneTitle: 'आपका सब्सक्रिप्शन रद्द हो गया है',
    doneBody: 'धन्यवाद — आपकी राय टीम तक पहुँचेगी।',
    doneUntil: '{date} तक आपको पूरा एक्सेस मिलता रहेगा।',
    doneNoSub: 'इस खाते पर कोई सक्रिय आवर्ती सब्सक्रिप्शन नहीं था, इसलिए आगे कोई शुल्क नहीं लिया जाएगा।',
    failed: 'हम सब्सक्रिप्शन रद्द नहीं कर सके। कुछ भी नहीं बदला है — कृपया {email} पर ईमेल करें, हम इसे संभाल लेंगे।',
  },
}

const id: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Dasbor',
    checkoutReturned: 'Anda kembali dari halaman pembayaran. Jika Anda sudah mengirim pembayaran, periksa status tagihan sebelum mencoba lagi.',
    sectionAria: 'Pilih tingkat bantuan yang tepat',
    intro: 'Mulailah dengan satu pertanyaan keuangan. Jelajahi pilihan yang didukung, pahami untung-ruginya, dan susun langkah berikutnya yang praktis bersama MyCIO. Rencana yang tersimpan memungkinkan Anda melanjutkan percakapan saat situasi Anda berubah.',
    notAdviceBefore: 'MyCIO membantu Anda merencanakan apa yang akan dilakukan; MyCIO tidak melakukan transaksi atau memindahkan uang Anda. Lihat ',
    whatWeModel: 'apa yang kami modelkan',
    notAdviceMiddle: ' dan ',
    seeExample: 'sebuah contoh',
    notAdviceAfter: ' sebelum memilih paket berbayar.',
    selectionBefore: 'Pilihan Anda: ',
    selectionAfter: '. Tinjau harga dan ketentuan sebelum melanjutkan; masuk ke akun tidak memulai pembayaran.',
    continueWith: 'Lanjutkan dengan {plan}',
    titleBefore: 'Bantuan untuk pertanyaan Anda. ',
    titleEm: 'Rencana untuk langkah selanjutnya.',
    titleAfter: '',
    body: 'Pilih seberapa banyak bantuan berkelanjutan yang Anda perlukan. Setiap tingkat memiliki batas cakupan; pembelian tidak membuat skenario yang tidak didukung menjadi tersedia.',
    renewalBody: 'Plus dan Wealth sudah termasuk tahun pertama Living Plan. Perpanjangan tahunan yang diumumkan adalah {plus} untuk Plus dan {wealth} untuk Wealth. Tinjau ketentuan perpanjangan sebelum membeli, dan hubungi kami untuk memastikan ketentuan layanan Lintas Batas.',
    footer: 'Harga ditampilkan dalam USD. Tinjau total, periode penagihan, dan pajak yang berlaku yang ditampilkan saat pembayaran sebelum mengonfirmasi pembayaran.',
    questionsLink: 'Ada pertanyaan tentang paket atau perpanjangan?',
    privacyLink: 'Kebijakan privasi',
  },
  checkout: {
    unavailable: 'Pembayaran belum tersedia saat ini — silakan coba lagi sebentar lagi.',
    betaConfirm: 'Saat ini Anda memiliki akses beta penuh.\n\nMembeli sekarang akan dikenai harga tahun pertama yang ditampilkan saat pembayaran. Untuk Plus atau Wealth, perpanjangan tahunan yang lebih rendah dimulai satu tahun setelah pembelian; tidak terkait dengan kapan akses beta berakhir.\n\nLanjutkan ke pembayaran?',
  },
  pay: {
    opening: 'Membuka pembayaran aman…',
    open: 'Selesaikan pembelian Anda di jendela pembayaran aman. Jika jendela tidak terbuka, izinkan pop-up untuk situs ini lalu muat ulang halaman.',
    returning: 'Mengembalikan Anda ke halaman harga…',
    failedBefore: 'Pembayaran tidak dapat dimuat. ',
    returnToPricing: 'Kembali ke halaman harga',
    failedAfter: ' dan coba lagi.',
    noCheckoutBefore: 'Tidak ada pembayaran untuk dibuka. ',
    choosePlan: 'Pilih paket',
    noCheckoutAfter: ' untuk memulainya.',
    unavailableBefore: 'Pembayaran sementara tidak tersedia. Silakan coba lagi sebentar lagi atau ',
    contactSupport: 'hubungi dukungan',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Batalkan langganan',
    body: 'Pembatalan menghentikan perpanjangan berikutnya. Anda tetap memiliki akses penuh hingga akhir periode yang sudah dibayar, dan tidak ada yang dihapus.',
    cta: 'Batalkan langganan saya',
    confirmTitle: 'Batalkan langganan Anda?',
    confirmBody: 'Rencana dan data Anda tetap seperti apa adanya. Anda dapat berlangganan lagi kapan saja.',
    reasonLabel: 'Apa alasan Anda membatalkan? (opsional)',
    reasonPlaceholder: 'Apa pun yang Anda tulis di sini langsung sampai ke tim.',
    confirm: 'Konfirmasi pembatalan',
    keep: 'Pertahankan langganan saya',
    working: 'Membatalkan…',
    doneTitle: 'Langganan Anda telah dibatalkan',
    doneBody: 'Terima kasih — masukan Anda diteruskan ke tim.',
    doneUntil: 'Anda tetap memiliki akses penuh hingga {date}.',
    doneNoSub: 'Tidak ada langganan berulang yang aktif di akun ini, jadi tidak akan ada tagihan lagi.',
    failed: 'Kami tidak dapat membatalkan langganan. Tidak ada yang berubah — silakan kirim email ke {email} dan kami akan menanganinya.',
  },
}

const tr: MyCIOBillingPagesCopy = {
  pricing: {
    backToDashboard: '← Panel',
    checkoutReturned: 'Ödeme sayfasından döndünüz. Bir ödeme gönderdiyseniz tekrar denemeden önce fatura durumunuzu kontrol edin.',
    sectionAria: 'Doğru destek düzeyini seçin',
    intro: 'Bir finansal soruyla başlayın. MyCIO ile desteklenen seçenekleri inceleyin, artılarını ve eksilerini anlayın ve uygulanabilir sonraki adımları belirleyin. Kaydedilen planlar, durumunuz değiştikçe konuşmaya devam etmenizi sağlar.',
    notAdviceBefore: 'MyCIO ne yapacağınızı planlamanıza yardımcı olur; işlem yapmaz ve paranızı aktarmaz. Ücretli bir plan seçmeden önce ',
    whatWeModel: 'neleri modellediğimize',
    notAdviceMiddle: ' ve ',
    seeExample: 'bir örneğe',
    notAdviceAfter: ' göz atın.',
    selectionBefore: 'Seçiminiz: ',
    selectionAfter: '. Devam etmeden önce fiyatı ve koşulları gözden geçirin; oturum açmak ödemeyi başlatmaz.',
    continueWith: '{plan} ile devam et',
    titleBefore: 'Sorunuz için yardım. ',
    titleEm: 'Sonrası için bir plan.',
    titleAfter: '',
    body: 'Ne kadar sürekli desteğe ihtiyacınız olduğunu seçin. Her seviyede kapsam sınırları geçerlidir; satın alma, desteklenmeyen bir senaryoyu kullanılabilir hâle getirmez.',
    renewalBody: 'Plus ve Wealth, Living Plan’ın ilk yılını içerir. Duyurulan yıllık yenileme ücreti Plus için {plus}, Wealth için {wealth}’dir. Satın almadan önce yenileme koşullarını inceleyin ve Sınır Ötesi bir hizmetin koşullarını netleştirmek için bizimle iletişime geçin.',
    footer: 'Fiyatlar USD cinsindendir. Ödemeyi onaylamadan önce ödeme sayfasında gösterilen toplamı, faturalandırma aralığını ve geçerli vergileri gözden geçirin.',
    questionsLink: 'Bir plan veya yenileme hakkında sorunuz mu var?',
    privacyLink: 'Gizlilik politikası',
  },
  checkout: {
    unavailable: 'Ödeme şu anda kullanılamıyor — lütfen biraz sonra tekrar deneyin.',
    betaConfirm: 'Şu anda tam beta erişiminiz var.\n\nŞimdi satın alırsanız ödeme sayfasında gösterilen ilk yıl fiyatı tahsil edilir. Plus veya Wealth için daha düşük yıllık yenileme, satın almadan bir yıl sonra başlar; beta erişiminin ne zaman biteceğine bağlı değildir.\n\nÖdemeye devam edilsin mi?',
  },
  pay: {
    opening: 'Güvenli ödeme açılıyor…',
    open: 'Satın alma işleminizi güvenli ödeme penceresinde tamamlayın. Pencere açılmadıysa bu site için açılır pencerelere izin verin ve sayfayı yenileyin.',
    returning: 'Fiyatlar sayfasına dönülüyor…',
    failedBefore: 'Ödeme sayfası yüklenemedi. ',
    returnToPricing: 'Fiyatlar sayfasına dönün',
    failedAfter: ' ve tekrar deneyin.',
    noCheckoutBefore: 'Açılacak bir ödeme yok. ',
    choosePlan: 'Bir plan seçerek',
    noCheckoutAfter: ' başlayabilirsiniz.',
    unavailableBefore: 'Ödeme geçici olarak kullanılamıyor. Lütfen biraz sonra tekrar deneyin veya ',
    contactSupport: 'destek ekibiyle iletişime geçin',
    unavailableAfter: '.',
  },
  cancel: {
    title: 'Aboneliği iptal et',
    body: 'İptal, gelecekteki yenilemeleri durdurur. Ödemesini yaptığınız dönemin sonuna kadar tam erişiminiz sürer ve hiçbir şey silinmez.',
    cta: 'Aboneliğimi iptal et',
    confirmTitle: 'Aboneliğiniz iptal edilsin mi?',
    confirmBody: 'Planınız ve verileriniz olduğu gibi kalır. İstediğiniz zaman yeniden abone olabilirsiniz.',
    reasonLabel: 'Neden iptal ediyorsunuz? (isteğe bağlı)',
    reasonPlaceholder: 'Buraya yazdıklarınız doğrudan ekibe ulaşır.',
    confirm: 'İptali onayla',
    keep: 'Aboneliğimi sürdür',
    working: 'İptal ediliyor…',
    doneTitle: 'Aboneliğiniz iptal edildi',
    doneBody: 'Teşekkürler — geri bildiriminiz ekibe iletildi.',
    doneUntil: '{date} tarihine kadar tam erişiminiz sürer.',
    doneNoSub: 'Bu hesapta etkin bir yinelenen abonelik yoktu, dolayısıyla başka bir ücret alınmayacak.',
    failed: 'Aboneliği iptal edemedik. Hiçbir şey değişmedi — lütfen {email} adresine e-posta gönderin, gerisini biz halledelim.',
  },
}

const byLanguage: Partial<Record<MyCIOLanguageCode, MyCIOBillingPagesCopy>> = {
  en, es, pt, fr, de, it, ru, zh, ja, ko, ar, hi, id, tr,
}

export function getMyCIOBillingPagesCopy(language: string | null | undefined): MyCIOBillingPagesCopy {
  return pickCopy(getMyCIOLanguage(language).code, byLanguage, en)
}

/** Fill `{name}` placeholders; a value the copy does not mention is ignored. */
export function fillCopy(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? values[k] : m))
}
